import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, errorMessage } from "@/lib/api";
import { label } from "@/lib/labels";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { LoadingState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, ClipboardList, Loader2, AlertTriangle, ArrowRight, Users, MapPin, CalendarDays } from "lucide-react";

const Frame = ({ company, children }) => (
  <div className="min-h-screen bg-background mist" data-testid="public-assessment-frame">
    <header className="border-b bg-card/80 backdrop-blur">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display font-bold">SI</div>
          <div className="leading-tight"><div className="font-display font-semibold">{company || "Stord Innkvartering"}</div><div className="text-[11px] text-muted-foreground">Behovskartlegging</div></div>
        </div>
        <Link to="/henvendelse" className="text-xs text-muted-foreground hover:text-foreground">Ny henvendelse</Link>
      </div>
    </header>
    <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">{children}</main>
  </div>
);

const visible = (q, answers) => !q.show_if || answers[q.show_if.key] === q.show_if.equals;

const Question = ({ q, value, onChange, error }) => {
  const id = `q-${q.key}`;
  const base = error ? "border-destructive focus-visible:ring-destructive" : "";
  const control = (() => {
    switch (q.type) {
      case "number":
        return <Input id={id} type="number" inputMode="numeric" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={`max-w-[200px] ${base}`} data-testid={`assessment-q-${q.key}`} />;
      case "textarea":
        return <Textarea id={id} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`assessment-q-${q.key}`} />;
      case "select":
        return (
          <Select value={value ?? ""} onValueChange={onChange}>
            <SelectTrigger id={id} className={`max-w-md ${base}`} data-testid={`assessment-q-${q.key}`}><SelectValue placeholder="Velg" /></SelectTrigger>
            <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        );
      case "boolean":
        return (
          <RadioGroup value={value === true ? "ja" : value === false ? "nei" : ""} onValueChange={(v) => onChange(v === "ja")} className="flex gap-3" data-testid={`assessment-q-${q.key}`}>
            {[["ja", "Ja"], ["nei", "Nei"]].map(([v, l]) => (
              <label key={v} htmlFor={`${id}-${v}`} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors duration-150 hover:bg-muted/50 ${(value === true && v === "ja") || (value === false && v === "nei") ? "border-primary bg-primary/5" : ""}`}>
                <RadioGroupItem value={v} id={`${id}-${v}`} data-testid={`assessment-q-${q.key}-${v}`} /> {l}
              </label>
            ))}
          </RadioGroup>
        );
      case "multiselect": {
        const arr = Array.isArray(value) ? value : [];
        return (
          <div className="flex flex-wrap gap-2" data-testid={`assessment-q-${q.key}`}>
            {(q.options || []).map((o) => {
              const on = arr.includes(o);
              return (
                <label key={o} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors duration-150 hover:bg-muted/50 ${on ? "border-primary bg-primary/5" : ""}`}>
                  <Checkbox checked={on} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} data-testid={`assessment-q-${q.key}-${o}`} /> {o}
                </label>
              );
            })}
          </div>
        );
      }
      case "date":
        return <Input id={id} type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`max-w-[220px] ${base}`} data-testid={`assessment-q-${q.key}`} />;
      default:
        return <Input id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`assessment-q-${q.key}`} />;
    }
  })();
  return (
    <div className="space-y-1.5" data-testid={`assessment-question-${q.key}`}>
      <Label htmlFor={id} className="text-sm">{q.label}{q.required && <span className="text-destructive"> *</span>}</Label>
      {control}
      {q.help && <p className="text-xs text-muted-foreground">{q.help}</p>}
      {error && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {error}</p>}
    </div>
  );
};

const fmtAnswer = (q, v) => {
  if (v === undefined || v === null || v === "") return "–";
  if (q.type === "boolean") return v ? "Ja" : "Nei";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};

export default function PublicAssessment() {
  const { token } = useParams();
  const a = useQuery({ queryKey: ["assessment", token], queryFn: () => get(`/public/assessments/${token}`), retry: false });
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => { if (a.data?.answers) setAnswers(a.data.answers); }, [a.data]);

  const sections = a.data?.form?.sections || [];
  const current = sections[step];
  const total = sections.length;

  const allQuestions = useMemo(() => sections.flatMap((s) => s.questions || []), [sections]);
  const answered = allQuestions.filter((q) => visible(q, answers) && answers[q.key] !== undefined && answers[q.key] !== "" && answers[q.key] !== null).length;
  const progress = total ? Math.round(((step) / total) * 100) : 0;

  const validateSection = (sec) => {
    const errs = {};
    (sec?.questions || []).forEach((q) => {
      if (q.required && visible(q, answers)) {
        const v = answers[q.key];
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) errs[q.key] = "Må fylles ut";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = useMutation({
    mutationFn: () => post(`/public/assessments/${token}`, { answers }),
    onSuccess: () => { setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikke sende inn. Kontroller svarene.")),
  });

  if (a.isLoading) return <Frame><LoadingState text="Henter skjema…" /></Frame>;
  if (a.error) {
    return (
      <Frame>
        <div className="rounded-2xl border bg-card p-8 text-center space-y-3" data-testid="assessment-invalid">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
          <h1 className="text-xl font-display font-semibold">Lenken er ugyldig eller utløpt</h1>
          <p className="text-sm text-muted-foreground">{errorMessage(a.error, "Fant ikke kartleggingen.")} Ta kontakt med din rådgiver for en ny lenke.</p>
          <Button asChild variant="outline"><Link to="/henvendelse">Send ny henvendelse</Link></Button>
        </div>
      </Frame>
    );
  }

  const data = a.data;
  const lead = data.lead || {};
  const alreadyDone = data.status === "utfylt" || done;

  if (alreadyDone) {
    return (
      <Frame company={data.company_name}>
        <div className="space-y-6 fade-up" data-testid="assessment-done">
          <div className="rounded-2xl border bg-card p-8 text-center space-y-3">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-7 w-7" /></div>
            <h1 className="text-2xl font-display font-semibold">Takk – kartleggingen er mottatt</h1>
            <p className="text-muted-foreground text-sm">Vi bruker svarene til å sette sammen et konkret løsningsforslag med moduloppsett, pris og leveringsplan.{data.submitted_at || done ? "" : ""}</p>
            {(data.submitted_at) && <div className="text-xs text-muted-foreground">Sendt inn {fmtDateTime(data.submitted_at)}</div>}
          </div>
          <div className="rounded-2xl border bg-card p-6">
            <h2 className="text-sm font-display font-semibold mb-3">Dine svar</h2>
            <div className="space-y-4">
              {sections.map((s, i) => (
                <div key={i}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1.5">{s.title}</div>
                  <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 text-sm">
                    {(s.questions || []).filter((q) => visible(q, answers)).map((q) => <div key={q.key} className="flex flex-col"><dt className="text-xs text-muted-foreground">{q.label}</dt><dd>{fmtAnswer(q, answers[q.key])}</dd></div>)}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame company={data.company_name}>
      <div className="space-y-6 fade-up">
        <div className="space-y-3">
          <Badge variant="outline"><ClipboardList className="h-3 w-3 mr-1" /> {label(data.need_type)}</Badge>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight">{data.form?.title || "Behovskartlegging"}</h1>
          {data.message && <p className="text-muted-foreground text-sm rounded-lg border-l-4 border-primary bg-primary/5 px-4 py-3" data-testid="assessment-message">{data.message}</p>}
          {!data.message && data.form?.description && <p className="text-muted-foreground text-sm">{data.form.description}</p>}
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
            {data.customer_name && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {data.customer_name}</span>}
            {lead.persons && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {lead.persons} personer</span>}
            {lead.location_address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lead.location_address}</span>}
            {lead.desired_start && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Oppstart {fmtDate(lead.desired_start)}{lead.duration_months ? ` · ${lead.duration_months} mnd` : ""}</span>}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Del {step + 1} av {total}: <span className="text-foreground font-medium">{current?.title}</span></span><span className="tabular">{answered} svar</span></div>
          <Progress value={progress} className="h-1.5" data-testid="assessment-progress" />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {sections.map((s, i) => (
            <button key={i} type="button" onClick={() => { if (i < step || validateSection(current)) setStep(i); }} className={`rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 ${i === step ? "bg-primary text-primary-foreground border-primary" : i < step ? "bg-primary/10 text-primary border-primary/30" : "hover:bg-muted"}`} data-testid={`assessment-step-${i}`}>{s.title}</button>
          ))}
        </div>

        <form className="rounded-2xl border bg-card p-5 sm:p-7 space-y-5" onSubmit={(e) => { e.preventDefault(); if (!validateSection(current)) return; if (step < total - 1) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } else submit.mutate(); }} data-testid="assessment-form">
          <h2 className="text-base font-display font-semibold">{current?.title}</h2>
          {(current?.questions || []).filter((q) => visible(q, answers)).map((q) => (
            <Question key={q.key} q={q} value={answers[q.key]} error={errors[q.key]} onChange={(v) => { setAnswers((x) => ({ ...x, [q.key]: v })); setErrors((e) => { const n = { ...e }; delete n[q.key]; return n; }); }} />
          ))}
          <div className="flex items-center justify-between pt-2">
            <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="assessment-prev-button">Tilbake</Button>
            <Button type="submit" disabled={submit.isPending} data-testid={step < total - 1 ? "assessment-next-button" : "assessment-submit-button"}>
              {submit.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}{step < total - 1 ? "Neste" : "Send inn kartlegging"}
            </Button>
          </div>
        </form>
        <p className="text-xs text-muted-foreground text-center">Svarene lagres når du sender inn. Felt merket * er påkrevd.</p>
      </div>
    </Frame>
  );
}
